// src/components/admin/flows/canvas/CanvasNode.js
import React from 'react';
import { NODE_TYPES, NODE_WIDTH, NODE_HEIGHT, PORT_RADIUS } from './nodeRegistry';

const NODE_DESCRIPTIONS = {
    trigger: 'starts the flow',
    condition: 'branch on a rule',
    action: 'perform an action',
    approval: 'wait for approval',
    delay: 'wait a duration',
    end: 'end of flow',
};

const CanvasNode = ({
    step,
    isSelected,
    onSelect,
    onDragStart,
    onPortMouseDown,
    onPortMouseUp,
    isDark,
}) => {
    const nodeType = NODE_TYPES[step.type] || NODE_TYPES.action;
    const Icon = nodeType.icon;

    // Compute subtitle: use specific config info if available, otherwise a descriptive default
    const getSubtitle = () => {
        if (step.type === 'trigger' && step.config?.event) {
            return step.config.event.replace(/_/g, ' ');
        }
        if (step.type === 'action' && step.config?.action_type) {
            return step.config.action_type.replace(/_/g, ' ');
        }
        if (step.type === 'approval' && step.config?.approvers?.type) {
            return step.config.approvers.type.replace(/_/g, ' ');
        }
        if (step.type === 'delay' && step.config?.duration_hours) {
            return `${step.config.duration_hours} ${step.config.duration_type || 'hours'}`;
        }
        if (step.type === 'condition' && step.config?.field) {
            return `${step.config.field} ${step.config.operator || ''}`;
        }
        return NODE_DESCRIPTIONS[step.type] || nodeType.description || '';
    };

    return (
        <div
            data-node-id={step.id}
            onClick={(e) => { e.stopPropagation(); }}
            onPointerDown={(e) => {
                // Select node on any pointer down (fires before click, more reliable)
                if (e.button === 0 && !e.target.closest('[data-port]')) {
                    e.stopPropagation();
                    onSelect(step.id);
                    onDragStart(e, step.id, step.position);
                }
            }}
            style={{
                position: 'absolute',
                left: step.position.x,
                top: step.position.y,
                width: NODE_WIDTH,
                height: NODE_HEIGHT,
                cursor: 'grab',
                zIndex: isSelected ? 10 : 1,
                pointerEvents: 'auto',
            }}
        >
            {/* Input port (top center) */}
            {step.type !== 'trigger' && (
                <div
                    data-port="input"
                    onPointerUp={(e) => { e.stopPropagation(); onPortMouseUp(e, step.id); }}
                    style={{
                        position: 'absolute',
                        top: -PORT_RADIUS,
                        left: '50%',
                        transform: 'translateX(-50%)',
                        width: PORT_RADIUS * 2,
                        height: PORT_RADIUS * 2,
                        borderRadius: '50%',
                        cursor: 'crosshair',
                        zIndex: 5,
                    }}
                    className={`border-2 transition-colors ${
                        isDark ? 'bg-gray-600 border-gray-500 hover:bg-blue-400' : 'bg-gray-300 border-gray-400 hover:bg-blue-500'
                    }`}
                />
            )}

            {/* Node body */}
            <div
                className={`w-full h-full rounded-xl border-2 flex items-center gap-3 px-3 transition-all select-none ${
                    nodeType.bgClass
                } ${
                    isSelected
                        ? `ring-2 ${nodeType.ringClass} border-transparent shadow-lg`
                        : `${nodeType.borderClass} hover:shadow-md`
                }`}
            >
                <div
                    className="flex-shrink-0 w-9 h-9 rounded-lg flex items-center justify-center"
                    style={{ backgroundColor: nodeType.color + '20' }}
                >
                    <Icon className="w-5 h-5" style={{ color: nodeType.color }} />
                </div>
                <div className="min-w-0 flex-1">
                    <p className={`text-sm font-semibold truncate ${isDark ? 'text-white' : 'text-gray-900'}`}>
                        {step.label}
                    </p>
                    <p className={`text-xs truncate ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                        {getSubtitle()}
                    </p>
                </div>
            </div>

            {/* Output port (bottom center) — not for end or condition */}
            {step.type !== 'end' && step.type !== 'condition' && (
                <div
                    data-port="output"
                    onPointerDown={(e) => { e.stopPropagation(); onPortMouseDown(e, step.id); }}
                    style={{
                        position: 'absolute',
                        bottom: -PORT_RADIUS,
                        left: '50%',
                        transform: 'translateX(-50%)',
                        width: PORT_RADIUS * 2,
                        height: PORT_RADIUS * 2,
                        borderRadius: '50%',
                        cursor: 'crosshair',
                        zIndex: 5,
                    }}
                    className={`border-2 transition-colors ${
                        isDark ? 'bg-gray-600 border-gray-500 hover:bg-blue-400' : 'bg-gray-300 border-gray-400 hover:bg-blue-500'
                    }`}
                />
            )}

            {/* Condition node: two output ports with labels */}
            {step.type === 'condition' && (
                <>
                    {/* True (Yes) port - left */}
                    <div
                        data-port="output"
                        onPointerDown={(e) => { e.stopPropagation(); onPortMouseDown(e, step.id, 'true'); }}
                        style={{
                            position: 'absolute',
                            bottom: -PORT_RADIUS,
                            left: `${30}%`,
                            transform: 'translateX(-50%)',
                            width: PORT_RADIUS * 2,
                            height: PORT_RADIUS * 2,
                            borderRadius: '50%',
                            cursor: 'crosshair',
                            zIndex: 5,
                        }}
                        className="bg-green-400 border-2 border-green-600 hover:bg-green-500"
                    />
                    <span
                        className="absolute text-[9px] text-green-600 dark:text-green-400 select-none pointer-events-none"
                        style={{ bottom: -(PORT_RADIUS + 14), left: '30%', transform: 'translateX(-50%)' }}
                    >
                        Yes
                    </span>

                    {/* False (No) port - right */}
                    <div
                        data-port="output"
                        onPointerDown={(e) => { e.stopPropagation(); onPortMouseDown(e, step.id, 'false'); }}
                        style={{
                            position: 'absolute',
                            bottom: -PORT_RADIUS,
                            left: `${70}%`,
                            transform: 'translateX(-50%)',
                            width: PORT_RADIUS * 2,
                            height: PORT_RADIUS * 2,
                            borderRadius: '50%',
                            cursor: 'crosshair',
                            zIndex: 5,
                        }}
                        className="bg-red-400 border-2 border-red-600 hover:bg-red-500"
                    />
                    <span
                        className="absolute text-[9px] text-red-600 dark:text-red-400 select-none pointer-events-none"
                        style={{ bottom: -(PORT_RADIUS + 14), left: '70%', transform: 'translateX(-50%)' }}
                    >
                        No
                    </span>
                </>
            )}
        </div>
    );
};

export default CanvasNode;
